import {createSlice} from "@reduxjs/toolkit";
import {productApi} from "../../configurator/services/productApi";
import {
    addElementsDisplayingOptions, addImportFeedback,
    manageConfiguratorState,
    removeElementsDisplayingOptions, removeElementToUpdateModules, setDisplayImportModal, setElementsDisplayingOptions
} from "../configuratorStateSlice";
import modulesApi from "../../configurator/services/modulesApi";
import {
    convertModuleDataToProductData,
    convertModuleSelectionsToAttributeSelections,
    getUpdatedModuleSelectionArray
} from "./productSelectionsSlice";
import {normalizeSkuExceptions} from "../exceptions/skuExceptionsSlice";
import {validSelection} from "../exceptions/disabledFieldsSlice";


export const productElementsSlice =  createSlice({
    name: 'productElements',
    initialState: {
        elements_with_selections: [],
        side_sections: [],
    },
    reducers: {
        setElementsWithSelections(state, action){
            state.elements_with_selections = action.payload;
        },
        setSideSections(state, action) {
          state.side_sections = action.payload;
        },
    },
});

export const { setElementsWithSelections, setSideSections } = productElementsSlice.actions;

/***
 * @description Adds an element, its product attributes and an empty selections array to
 * ProductElementsReducer elements_with_Selections, stored under a hash element-{productFamily}-elementCount.
 * Attribute selections will have the first product_attribute with selections set to "".
 * @param product - product of which attributes to get.
 */
export const addElement = (product, passedSection = null, passedSelections = null) => async (dispatch, getState) => {
    let currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const productResult = dispatch(productApi.endpoints.getProduct.initiate(product.id));
    const {data: productData } = await productResult;
    const productAttributes = productData.product.product_attributes;
    const parentElementSelections = getState().productDataReducer.attribute_selections;

    let selections = [];

    selections = generateElementSelections(productAttributes, passedSelections, parentElementSelections);
    const section = isNaN(passedSection) || passedSection === null ? getLatestSectionNumber(currentElements) : passedSection;
    const elementToAdd = Object.assign({}, product, {
        product_attributes: productAttributes,
        attribute_selections: selections,
        element_order: Object.keys(currentElements).length + 1,
        quote_item_module_with_selections: [],
        per_module_selections: [],
        sku_exceptions: normalizeSkuExceptions(productData.product.sku_exceptions),
        section_number: section,
        // quantity: 1,
        // currency: currency
    })

    // validation for exceptions
    const validAttributeSelections = getValidatedElementSelections(elementToAdd, dispatch);
    const elementWithValidSelections = Object.assign({}, elementToAdd, {attribute_selections: validAttributeSelections})

    currentElements.push(elementWithValidSelections);
    dispatch(setElementsWithSelections(currentElements));
}

// Compare and update selectiond if they are invalid
export const getValidatedElementSelections = (elementToValidate, dispatch) => {
    const validAttributeSelections = [];
    for( const attribute of elementToValidate.attribute_selections) {
        const valid = dispatch(validSelection(attribute.selection, attribute,  elementToValidate));
        if(!valid) {
            const updatedSelection = Object.assign({}, attribute, {selection: ""});
            validAttributeSelections.push(updatedSelection);
        } else {
            validAttributeSelections.push(attribute);
        }
    }
    return validAttributeSelections;
}

export const getLatestSectionNumber = (elements) => {
    let sectionNum = 1;
    if(elements.length  > 0) {
        sectionNum = elements[elements.length - 1].section_number;
    }
    return sectionNum;
}

/***
 * @description returns element selections for either step by step or shapes using
 * @returns {*[]}
 */
export const generateElementSelections = (productAttributes, passedSelections, parentSelections) => {
    let selections = [];
    const stepByStep = passedSelections == null;
    let firstProductAttribute = productAttributes[0];
    if(stepByStep) {
        // stepByStep doesnt need to fill all selections, only the first, and second if first is preselected
        // default value should always be ""
        if (firstProductAttribute.preselected) {
            selections = [
                firstProductAttribute,
                Object.assign({}, productAttributes[1], {
                    selection: ""
                })
            ]
        } else {
            selections = [Object.assign({}, productAttributes[0], {
                selection: ""
            })]
        }
    } else {
        // non stepByStep is only sued for generate shapes, since the full selection SHOULD
        // already be passed. The tricky part here is figuring out what attribute matches each selection.
        // As of writing this the first will always be a selection, and second will sometimes be. The rest
        // will be filled in from parent selections.
        let index = 0;
        for( let attribute of productAttributes) {
            let attributeWithSelection;
            if(index < passedSelections.length && passedSelections[index]) {
                if(attribute.values.includes(passedSelections[index])) {
                    attributeWithSelection = Object.assign({}, attribute, {
                        selection: passedSelections[index]
                    });
                } else {
                    console.log('ERROR no matching selection')
                }
            } else {
                let value = getParentAttributeSelectionIfExists(parentSelections, attribute);
                attributeWithSelection = Object.assign({}, attribute, {
                    selection: value,
                    hidden: true
                })
            }
            selections.push(attributeWithSelection);
            index++;
        }

    }
    return selections;
}

/***
 * @description Removes an element from ProductElementsReducer elements_with_selections base on the key.
 * @param key - the key to remove
 */
export const removeElement = (index) => (dispatch, getState) => {
    const currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    let elementsDisplayingOptions = Object.assign([], getState().configuratorStateReducer.elementsDisplayingOptions);
    currentElements.splice(index, 1);
    elementsDisplayingOptions = elementsDisplayingOptions.filter(edo => edo != index);
    dispatch(setElementsDisplayingOptions(elementsDisplayingOptions));
    dispatch(setElementsWithSelections(currentElements));
    dispatch(manageConfiguratorState());
}
/**
 * @description removes all elements. manageConfiguratorState must be called manually after
 * to display results.
 *
 */
export const clearElements = () => (dispatch, getState) => {
    dispatch(setElementsDisplayingOptions([]));
    dispatch(setElementsWithSelections([]));
}

/***
 * @description Adds a selection to the target elements attribute_selections array.
 * @param elementKey {int} The element to add to
 * @param attribute {hash} The attribute to add
 * @param selection {string} The selection to add
 */
export const updateElementSelection = (elementKey, attribute, selection) => (dispatch, getState) => {
    const currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const attribute_selections = getState().productDataReducer.attribute_selections;
    currentElements[elementKey] = getUpdatedElement(elementKey, attribute, selection, currentElements, attribute_selections);

    dispatch(setElementsWithSelections(currentElements));
    dispatch(shouldDisplayElementOptions(elementKey, currentElements[elementKey]));
    dispatch(manageConfiguratorState());
}
/***
 * @description Does the main work for merging existing selections with updated selection
 * @returns attribute selections with updated values and the next ready to go fo step by step
 */
export const getUpdatedElement = (elementKey, attribute, selection, currentElements, attribute_selections) => {
    let currentElement = Object.assign({}, currentElements[elementKey]);
    if(!currentElement) { return; }

    const updatedSelections = getUpdatedElementSelections(attribute, selection, currentElement);
    if(!updatedSelections) { return;}
    currentElement = Object.assign({}, currentElement, {
        attribute_selections: updatedSelections
    });

    return setNextElementSelection(currentElement, attribute_selections)
}

/***
 * @description Almost identical to updateElementSelections - this dispatcher will take an array and update
 * multiple selections with a single dispatch call. The main difference is this will not check for step by step
 * logic for displaying options.
*/
export const updateBulkElementSelections = (selections) => (dispatch, getState) => {
    const currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const attribute_selections = getState().productDataReducer.attribute_selections
    for(let sel of selections) {
        const elementKey = sel.key;
        const attribute = sel.attribute;
        const selection = sel.value
        currentElements[elementKey] = getUpdatedElement(elementKey,attribute, selection, currentElements, attribute_selections);
    }
    dispatch(setElementsWithSelections(currentElements));
    dispatch(manageConfiguratorState());
}

/***
 * @description update selections for the current element and returns an attribute_selections array
 * @returns updated attribute_selections
 */
export const getUpdatedElementSelections = (attribute, selection, currentElement, passedSelections = null) => {
    if(!currentElement || !currentElement.attribute_selections){
        return null;
    }
    let attributeIndex = currentElement.attribute_selections.findIndex(attr => attr.id == attribute.id );
    const productAttributes = currentElement.product_attributes;
    let currentSelections = passedSelections ? passedSelections : [].concat(currentElement.attribute_selections);

    // handle cases where the attribute index isnt found - such as optional attributes
    // -1 means not found
    if(attributeIndex == -1){
        const matchingProductAttribute = productAttributes.find(pa => pa.optional && pa.id == attribute.id);
        if(!matchingProductAttribute){return false;}
        currentSelections.push(matchingProductAttribute);
        attributeIndex = currentSelections.findIndex(attr => attr.id == attribute.id );
    }

    currentSelections[attributeIndex] = Object.assign({}, attribute, {
        selection: selection
    });
    return currentSelections;
}

export const setNextElementSelection = (currentElement, parentSelections) => {
    // get and set the next element attribute to selection ""
    let updatedElement;
    const currentSelections = currentElement.attribute_selections;
    const elementAttributes = currentElement.product_attributes.filter(pa => !pa.optional);
    const nextAttribute = elementAttributes[currentElement.attribute_selections.length];
    let hidden = false;
    if(nextAttribute) {
        const parentSelection = getParentAttributeSelectionIfExists(parentSelections, nextAttribute)
        hidden = parentSelection != "";
        currentSelections.push(Object.assign({}, nextAttribute, {
            selection: parentSelection,
            hidden: hidden,
        }))
        updatedElement = Object.assign({},currentElement, {
            attribute_selections: currentSelections
        });
    } else {
        return currentElement;
    }

    if(hidden){
        updatedElement = setNextElementSelection(updatedElement, parentSelections);
    }

    return updatedElement;
}

export const elementParentUpdatedSelection = (attribute, selection) => (dispatch, getState) => {
    const elements = getState().productElementsReducer.elements_with_selections;
    const disabledFields = getState().disabledFieldsReducer.disabledElementFields;
    let count = 0;
    const updatedElements = [];
    for(let element of elements) {
        let currentElement = Object.assign({}, element);
        let attr = element.attribute_selections.find(eas => eas.internal_name === attribute.internal_name);
        let updatedSelections = null;
        if( attr ) {
            updatedSelections = getUpdatedElementSelections(attr, selection, element);
        } else {
            // check if theres an options
            attr = element.product_attributes.find(pa => pa.optional && pa.internal_name === attribute.internal_name);
            if(attr) {
                updatedSelections = getUpdatedElementSelections(attr, selection, element);
            }
        }

        // we need to check if a parent selection invalidates an exception,
        // unless the selection is optional and one is required, in which case we should leave it empty
        if(attr) {
            const newSelectionExceptions = element.sku_exceptions.filter(se => se.exceptionFields[attr.id]);
            for (const exception of newSelectionExceptions) {
                if (exception.exceptionFields[attr.id].includes(selection)) {
                    for(const fieldId of Object.keys(exception.exceptionFields)) {
                        if(fieldId != attr.id) {
                            const matchingAttribute = element.attribute_selections.find(eas => eas.id == fieldId);
                            const matchingAttributeSelection = matchingAttribute ? matchingAttribute.selection : ""
                            const requiredOptionalException = attr.optional && !matchingAttribute.optional
                            if(!requiredOptionalException && exception.exceptionFields[fieldId].includes(matchingAttributeSelection)) {
                                updatedSelections = getUpdatedElementSelections(matchingAttribute, "", element, updatedSelections)
                            }
                        }
                    }
                }
            }
        }

        if(updatedSelections) {
            currentElement = Object.assign({}, currentElement, {
                attribute_selections: updatedSelections
            });
        }
        // for another check we validate all changes
        const validatedSelections = getValidatedElementSelections(currentElement, dispatch);
        currentElement = Object.assign({}, currentElement, {
            attribute_selections: validatedSelections
        });

        updatedElements.push(currentElement);
        count++;
    }
    dispatch(setElementsWithSelections(updatedElements));
    dispatch(manageConfiguratorState());
}


export const getParentAttributeSelectionIfExists = (parentSelections, attribute) => {
    let selection = "";
    const matching = parentSelections.find(pas => pas.internal_name == attribute.internal_name);
    if(matching){
        selection = matching.selection
    }
    return selection;
}

export const hasMatchingParentAttribute = (parentAttributes, attribute) => {
    const matching = parentAttributes.find(pas => pas.internal_name === attribute.internal_name);
    return !!matching;
}

export const shouldDisplayElementOptions = (id, element) => (dispatch, getState) => {
    const requiredAttributesCount = element.product_attributes.filter(pa => !pa.optional && !pa.per_module).length;
    const requiredSelectionCount = element.attribute_selections.filter(as => !as.optional && !as.per_module && as.selection).length;
    const isDisplaying = getState().configuratorStateReducer.elementsDisplayingOptions.includes(id);
    if(requiredAttributesCount === requiredSelectionCount){
        if(!isDisplaying) {
            dispatch(addElementsDisplayingOptions(id))
            dispatch(copyParentOptionSelectionsToElements())
        }
        const elementModulesToUpdate = getState().configuratorStateReducer.elementsThatShouldUpdateModules;
        if(elementModulesToUpdate.includes(id)){
            dispatch(getElementModules(id));
        }
    } else {
        dispatch(removeElementsDisplayingOptions(id))
    }
}

export const reorderElements = (elementId, indexChange) => (dispatch, getState) => {
    const elements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const targetIndex = elementId + indexChange;
    if(targetIndex < 0  || targetIndex > elements.length - 1){
        return;
    }

    const elementA = elements[elementId];
    elements[elementId] = elements[targetIndex];
    elements[targetIndex] = elementA;

    dispatch(setElementsWithSelections(elements));
    dispatch(manageConfiguratorState())
}

export const getElementModules = (elementId) => async (dispatch, getState) => {
    const element =  getState().productElementsReducer.elements_with_selections[elementId];
    const catalog = getElementCatalog(element);
    dispatch(removeElementToUpdateModules(elementId))
    const response = await modulesApi.getModulesFromCatalog(catalog);
    if(response) {
        dispatch(setElementModuleSelections(elementId, response.modules));
    }
}

export const getElementCatalog = (element) => {
    const family = element.family;
    const selections = element.attribute_selections;
    const filteredSelections = selections.filter(s => !s.per_module);
    const selectionsCatalog = filteredSelections.map(fs => fs.selection).join("");
    return family + selectionsCatalog;
}

export const setElementModuleSelections = (elementId, modules) => (dispatch, getState) => {
    const elements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    // add ids to modules
    const modulesWithIds = modules.map((mod, i) => Object.assign({}, mod, { module_id: i, selections: []}))
    elements[elementId] = Object.assign({}, elements[elementId], {
        per_module_selections: modulesWithIds,
    })
    dispatch(setElementsWithSelections(elements));
}

export const elementModuleSelectionsChanged = (elementId, value, module_key, module_id) => (dispatch, getState) => {
    const currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const currentElement = Object.assign({}, currentElements[elementId]);
    const currentSelections = currentElement.per_module_selections;
    const updatedSelections = getUpdatedModuleSelectionArray(currentSelections, module_id, module_key, value);
    const moduleAttributes = currentElement.product_attributes.filter(pa => pa.optional && pa.per_module && !pa.read_only);

    const normalizedModules = convertModuleDataToProductData(updatedSelections, moduleAttributes);
    const moduleAttributeSelections = convertModuleSelectionsToAttributeSelections(moduleAttributes, updatedSelections);

    const elementAttributeSelections = Object.assign([], currentElement.attribute_selections);
    for(let moduleSelection of moduleAttributeSelections){
        const foundSelection = elementAttributeSelections.findIndex(eas => eas.id == moduleSelection.id);
        const shouldRemove = moduleSelection.selection === "";
        if(foundSelection === -1) {
            if(!shouldRemove) {
                elementAttributeSelections.push(moduleSelection);
            }
        } else {
            if(shouldRemove){
                elementAttributeSelections.splice(foundSelection, 1);
            } else {
                elementAttributeSelections[foundSelection] = moduleSelection;
            }
        }
    }


    currentElements[elementId] = Object.assign({}, currentElement, {
        per_module_selections: updatedSelections,
        quote_item_module_with_selections: normalizedModules,
        attribute_selections: elementAttributeSelections
    });

    dispatch(setElementsWithSelections(currentElements));
    dispatch(manageConfiguratorState());

};


export const setElementsWithSelectionsFromLoad = (elements, parentAttributes) => dispatch => {
    // TODO FIX PER MODULE SELECTIONS!!!!!!!!!
    const generatedElements = [];
    elements.forEach((element, i) =>  {
        // add hidden attribute to attribute selections if it matches a parent attribute
        const selections = element.attribute_selections.map(as => {
            const matchingParent = parentAttributes.find(pa => pa.internal_name === as.internal_name);
            if(matchingParent) {
                return Object.assign({}, as, { hidden: true})
            } else {
                return as
            }
        })

        const e = Object.assign({}, element, {
            element_id: i,
            perModuleSelections: [],
            quote_item_module_with_selections: [],
            attribute_selections: selections,
            sku_exceptions: normalizeSkuExceptions(element.sku_exceptions)
        })
        generatedElements.push(e);
    })
    dispatch(setElementsWithSelections(generatedElements));
}

export const copyParentOptionSelectionsToElements = () => (dispatch, getState) => {
    const elements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    const parentAttributes = Object.assign([], getState().productDataReducer.attribute_selections);
    const optionalParentAttributes = parentAttributes.filter(pa => pa.optional && !pa.per_module && pa.selection);
    for (let parentOptional of optionalParentAttributes) {
        let elementIndex = 0;
        for(let element of elements) {
            const matchingElementOptional = element.product_attributes.find(pa => pa.optional && pa.internal_name === parentOptional.internal_name);
            if(matchingElementOptional) {
                dispatch(updateElementSelection(elementIndex, matchingElementOptional, parentOptional.selection))
            }
            elementIndex++;
        }
    }
}



export const elementSectionChanged = (elementKey, value) => (dispatch, getState) => {
    const currentElements = Object.assign([], getState().productElementsReducer.elements_with_selections);
    let currentElement = Object.assign({}, currentElements[elementKey], {
        section_number: value
    });

    currentElements[elementKey] = currentElement;
    dispatch(setElementsWithSelections(currentElements));
    dispatch(manageConfiguratorState());
}

export const createElementsFromCsv = (results) => (dispatch, getState) => {
    // results contain header row so we skip the first
    const product_elements = getState().productDataReducer.product_elements;
    const SECTION_KEY = "Section";
    const FAMILY_KEY = "Fam Code";
    const LEGACY_FAMILY_KEY = "Family"

    // store created elements here
    const elements = [];
    // grab parent attributes to autofill majority of element selections
    const parentAttributes = Object.assign([], getState().productDataReducer.attribute_selections);
    const parentFamily = getState().productDataReducer.family;
    const elementsDisplayingOptions = [];
    let elementId = 0;
    for(const row of results.data) {
        if(row['ID']) {
            let family = row[FAMILY_KEY]

            // support legacy import sheets which do not pass family_key, but rather two separate columns
            if(!family) {
                family = row[LEGACY_FAMILY_KEY]
                // hardcode value for replacing lines
                family = family.replace("Lines", "LN1");
            }

            // #566 Family has been added to the tables so we now use that instead of guessing
            // Import sheet might have multiple different familys for different line items, so
            // we need to filter different families out
            if(!parentFamily.includes(family)){
                continue
            }
            // row[ID] sometimes contains family defining attributes and sometimes doesnt
            // we have to combine each before we can even try to figure out which element to create
            // eg ID might be D8, so we add LN1 to make LN1D8 -> correct family is LN1D
            // eg2 Id might be V2P, so we add LN1 to make LN1V2P -> correct family is LN1
            let elementCatalog = family + row['ID'];
            // as noted above we have to figure out which element to create, and since the family code
            // might be 3 or 4 letters, we start with 4 and work backwards to prevent false positives for LN1.
            const FAMILY_CHAR_INDEX = 4;
            let product_element;
            for(let i = FAMILY_CHAR_INDEX; i > 0; i--) {
                const potentialFamily = elementCatalog.substring(0, i);
                // try to find a family, if successful then leave the loop
                const temp = product_elements.find(pe => pe.family === potentialFamily);
                if(temp) {
                    product_element = temp;
                    break;
                }
            }

            if(!product_element) {
                const error = "Unable to find a element matching \"" + family + "\" for row " + (elementId + 1);
                dispatch(addImportFeedback(error))
                return;
            }
            let element = Object.assign({}, product_element)
            // ID is a partial catalog number, so we can remove the family from it to get attributes
            elementCatalog = elementCatalog.replace(element.family, "");
            const element_selections = [];
            for (const attribute of element.product_attributes) {
                let attributeToAdd;
                const overrideAttribute = elementConfigurationOverrides(row, attribute, elementId, elementCatalog, element.family);
                const matchingParentAttribute = parentAttributes.find(pa => pa.internal_name == attribute.internal_name);
                if(overrideAttribute){
                   attributeToAdd = overrideAttribute;
                // matching element means we copy it, otherwise we need to find the value based on the csv
                } else if (matchingParentAttribute) {
                    // check if the selection is disabled - a bit janky since normally the element already exists
                    // since this element isnt actually in the store we have to assemble it with would be values

                    // Setup the attribute
                    attributeToAdd = Object.assign({}, attribute, {
                        selection: matchingParentAttribute.selection,
                        hidden: true
                    });
                    // setup attribute_selections
                    const tempSelections = Object.assign([], element_selections);
                    tempSelections.push(attributeToAdd);

                    // create a temp element
                    const tempElement = Object.assign({}, element, {
                        attribute_selections: tempSelections,
                        sku_exceptions: normalizeSkuExceptions(element.sku_exceptions)
                    })

                    // validate element selection and handle attributeToAdd based on result
                    const valid = dispatch(validSelection(attributeToAdd.selection, attributeToAdd,  tempElement));
                    if(!valid) {
                        attributeToAdd = Object.assign({}, attribute, {
                            selection: "",
                            hidden: true
                        });
                    }
                } else {
                    const rowResult = getSelectionFromCsvRow(row, attribute, elementId, elementCatalog);
                    if(rowResult.error) {
                        dispatch(addImportFeedback(rowResult.error))
                        return;
                    }else if(attribute.optional && !rowResult.selection) {
                        continue;
                    } else {
                        attributeToAdd = rowResult.selection;
                        elementCatalog = rowResult.elementCatalog;
                    }
                }

                element_selections.push(attributeToAdd);
            }

            // assign attribute selections and add to our array s
            element = Object.assign({}, element, {
                attribute_selections: element_selections,
                quote_item_module_with_selections: [],
                sku_exceptions: normalizeSkuExceptions(element.sku_exceptions),
                section_number: row[SECTION_KEY]
            })
            elements.push(element);
            elementsDisplayingOptions.push(elementId);
            elementId++;
        }
    }

    dispatch(setElementsWithSelections([]))
    dispatch(setElementsWithSelections(elements))
    dispatch(setElementsDisplayingOptions(elementsDisplayingOptions))
    dispatch(addImportFeedback("Successfully imported " + elements.length + " elements"))
    dispatch(manageConfiguratorState());

}
/***
 Allows for attribute injection to meet specific business needs, overrides inherited values passed
 from the parent for specific products
 ***/
export const elementConfigurationOverrides = (row, attribute, elementId, elementCatalog, elementFamilyCode) => {
    const famCode = row['Fam Code'];
    const detail = row['Detail'];

    // Begin overrides
    // #641 - LNW only with a source BP (LNWD), always force ceiling = R
    if(elementFamilyCode === "LNWD" && attribute.internal_name === "ceiling" && famCode === "LNW" && detail === "Battery Pack") {
        return Object.assign({}, attribute, {selection: "R",  hidden: true})
    }

    // #641 - for both LN1 and LNW, catalogs containing N should be forced unlit:
    // Distribution = U0, CRI = 0, CCT = 00
    if((famCode === "LN1" || famCode === "LNW") && elementCatalog.slice(elementCatalog.length - 1) === "N"){
        switch (attribute.internal_name) {
            case 'distribution' :
                return Object.assign({}, attribute, {selection: "U0",  hidden: true})
                break;
            case 'cri' :
                return Object.assign({}, attribute, {selection: "0",  hidden: true})
                break;
            case 'cct' :
                return Object.assign({}, attribute, {selection: "00",  hidden: true})
                break;
            default:
                return null;
        }
    }

    // default nothing
    return null;

}

export const getSelectionFromCsvRow = (row, attribute, elementIndex, elementCatalog = "") => {
    let selection;
    let error;

    //Element catalog contains the remaining selections we need to select, so look for values
    // that match and fill them in
    for(let value of attribute.values.split(',')) {
        if(!attribute.optional) {
            const elementSubstring = elementCatalog.substring(0, value.length);
            if (elementSubstring === value) {
                // if we find a matching substring, this is the correct selection, remove from catalog and exit
                selection = Object.assign({}, attribute, {selection: value});
                elementCatalog = elementCatalog.substring(value.length);
                break;
            }
        } else {
            // if first character is - we are onto option values
            if(elementCatalog[0] === "-") {
                // 2nd element for length of value
                const elementSubstring = elementCatalog.substring(1, 1 + value.length);
                if (elementSubstring === value) {
                    // if we find a matching substring, this is the correct selection, remove from catalog and exit
                    selection = Object.assign({}, attribute, {selection: value});
                    elementCatalog = elementCatalog.substring(value.length + 1);
                    break;
                }

            }
        }
    }
    if (!selection && !attribute.optional){
        error = "Unable to find a valid selection for " + attribute.name + " on row " + (elementIndex + 1) + ". Check catalog ID then try again.";
    }
    return {selection: selection, error: error, elementCatalog: elementCatalog};
}

export const mapSectionsToSide = (shapes) => (dispatch, getState) => {
    const mapped = {};
    // map sides to sections and remove duplicates using new Set
    for(let shape of shapes) {
        const current = mapped[shape['SIDE_ID']] ? mapped[shape['SIDE_ID']] : [];
        mapped[shape['SIDE_ID']] = [...new Set(current.concat(shape['ELEM_SECT']))]
    }
    dispatch(setSideSections(mapped));

}

export const updateModuleSelections = (elements, section, attribute, selection) => (dispatch, getState) => {
    const disabledElementFields = getState().disabledFieldsReducer.disabledElementFields;
    const selections = [];
    for(let element of elements) {
        if(element.section_number === section) {
            const matchingAttribute = element.product_attributes.find(pa => pa.internal_name === attribute.internal_name);
            if(matchingAttribute) {
                // we also want to verify the selection isnt disabled on the element before we add it
                const disabledField = disabledElementFields[element.elementKey].find(df => df.id === matchingAttribute.id);
                const disabledSelections = disabledField ? disabledField.disabledSelections : [];
                if(!disabledSelections.includes(selection)) {
                    selections.push({key: element.elementKey, attribute: matchingAttribute, value: selection})
                }
            }
        }
    }
    dispatch(updateBulkElementSelections(selections));
};

const reducer = productElementsSlice.reducer;
export default reducer